import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showInfoToast } from '@Helpers';
import { cn } from '@Utils';

import { ModalComponent, ModalLoading, ModalSuccess } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFetchData } from '@CustomHooks';
import { DataSupplier, PlanOption } from '@Models';
import { bopsApi } from '@Network';
import config from '../../config';
import { getPlanMeta } from './planMeta';

interface SupplierPlanModalProps {
  supplier: DataSupplier;
  onClose: () => void;
  onUpdated: () => void;
}

interface FormFields {
  planId: string;
}

const PlanOptionCard = ({
  plan,
  selected,
  onSelect,
}: {
  plan: PlanOption;
  selected: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const meta = getPlanMeta(plan.name);
  // allows_custom means something different for GS1: all 3 of its plans
  // have it set (it's what makes custom_dashboard_ids resolve, not "needs
  // manual setup" like Logyca's single Avanzado plan) — GS1 suppliers can
  // self-service select any of them.
  const disabled = plan.allows_custom && !config.isGs1Flavor;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
        disabled && 'cursor-not-allowed opacity-60',
        !disabled && (selected ? 'border-brand-600 bg-brand-50' : 'border-gray-200 hover:border-brand-300'),
        disabled && 'border-gray-200',
      )}>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900">{plan.display_name}</div>
        {meta.description && <div className="mt-0.5 text-sm text-gray-500">{meta.description}</div>}
        {disabled && <div className="mt-1 text-xs text-gray-400">{t('Supplier.PlanRequiresManualSetup')}</div>}
      </div>
      {!disabled && (
        <div
          className={cn(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2',
            selected ? 'border-brand-600' : 'border-gray-300',
          )}>
          {selected && <div className="h-2.5 w-2.5 rounded-full bg-brand-600" />}
        </div>
      )}
    </button>
  );
};

const SupplierPlanModal = ({ supplier, onClose, onUpdated }: SupplierPlanModalProps) => {
  const { t } = useTranslation();
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);

  const { data: planOptions } = useFetchData(bopsApi.getSupplierPlanOptions);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormFields>({ defaultValues: { planId: supplier.plan_id ?? '' } });

  const planSelected = watch('planId');
  const selectedPlanAllowsCustom = config.isGs1Flavor
    ? false
    : (planOptions ?? []).find((p) => p.id === planSelected)?.allows_custom ?? false;
  const addonsCount = supplier.enabled_addons?.length ?? 0;
  // The backend always resets enabled_addons_ids on PUT /plan, even when
  // resubmitting the same plan_id — so the warning is tied purely to
  // whether the supplier currently has active add-ons, not to whether the
  // selection changed.
  const willClearAddons = addonsCount > 0;

  const onSubmit = (data: FormFields) => {
    if (!supplier.id) return;
    setOpenLoading(true);
    bopsApi
      .updateSupplierPlan(supplier.id, { plan_id: data.planId, partition_id: supplier.partition_id ?? undefined })
      .then(({ applied }) => {
        setOpenLoading(false);
        if (!applied) {
          // Blocked: the transition needs Data's manual work first (e.g.
          // Intermedio/Avanzado → Básico) — client_plan was left untouched.
          showInfoToast(t('Supplier.PlanChangePending'));
          onUpdated();
          return;
        }
        setOpenSuccess(true);
        setTimeout(() => {
          setOpenSuccess(false);
          onUpdated();
        }, 1500);
      })
      .catch(() => {
        setOpenLoading(false);
        showErrorToast(t('Supplier.PlanChangeError'));
      });
  };

  return (
    <>
      <ModalComponent isOpen onClose={onClose} title={t('Supplier.ChangePlanTitle')} closeClickOutside={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="-mt-2 mb-4 text-sm text-gray-500">{supplier.display_name}</p>

          {willClearAddons && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
              <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
              <span>{t('Supplier.ChangePlanWarning', { count: addonsCount })}</span>
            </div>
          )}

          <Controller
            control={control}
            rules={{ required: true }}
            name="planId"
            render={({ field: { onChange } }) => (
              <div role="radiogroup" aria-label={t('Supplier.SelectPlan')} className="flex flex-col gap-2">
                {(planOptions ?? []).map((plan) => (
                  <PlanOptionCard
                    key={plan.id}
                    plan={plan}
                    selected={plan.id === planSelected}
                    onSelect={() => onChange(plan.id)}
                  />
                ))}
              </div>
            )}
          />
          {errors.planId && <p className="mt-2 text-sm text-red">{t('Supplier.PlanRequired')}</p>}

          <div className="flex gap-2 justify-center mt-6">
            <TrackedButton
              variant="outline"
              buttonName="SUPPLIER_PLAN_CHANGE_CANCELED"
              className="w-full bg-white"
              onClick={onClose}
              isSubmit={false}>
              {t('Global.Canceled')}
            </TrackedButton>
            <TrackedButton
              buttonName="SUPPLIER_PLAN_CHANGED"
              isSubmit
              className="w-full"
              disabled={selectedPlanAllowsCustom}>
              <div className="text-white">{t('Supplier.SaveChanges')}</div>
            </TrackedButton>
          </div>
        </form>
      </ModalComponent>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center -mt-4">
          <span className="text-[18px]">{t('Supplier.PlanChangeSuccess')}</span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center -mt-[9px]">{t('Supplier.LoadingCreate')}</div>
      </ModalLoading>
    </>
  );
};

export default SupplierPlanModal;
